// Release history rules shared by the plate hero and the Plate History
// timeline, so both always tell the same story.
//
// Two different facts, never conflated:
//   SERIES        what the mark's format or age identifier implies: F80 FER is
//                 an F-prefix mark, a series that ran August 1988 to July 1989.
//                 Context only, never the date this plate was issued.
//   FIRST RELEASE when this individual mark left DVLA, and how. Shown only
//                 where the plate data records it (DVLA auction or DVLA
//                 fixed-price sale). F80 FER: September 2012, DVLA auction.
//
// Where the first release is not recorded, nothing stands in for it: not the
// series date, not the first vehicle sighting, not the first resale.

import { FirstIssueState, PlateDetail } from '../models/plateDetail';
import { datelessSeriesYear } from './plate-year';

export type ReleaseMethod = 'DVLA auction' | 'DVLA fixed price';

export interface FirstRelease {
  /** YYYY-MM-DD */
  date: string;
  /** "September 2012" */
  label: string;
  method: ReleaseMethod;
}

/** The recorded first release, or null when there is no record of one. */
export function firstReleaseOf(pd: PlateDetail | null | undefined): FirstRelease | null {
  const fi = pd?.firstIssue;
  if (!fi || !fi.date || !fi.label) return null;
  // The method comes from the record's state, never inferred from a price or
  // a seller name.
  const method: ReleaseMethod | null =
    fi.state === FirstIssueState.ISSUED_AT_AUCTION
      ? 'DVLA auction'
      : fi.state === FirstIssueState.ISSUED_AT_FIXED_PRICE
      ? 'DVLA fixed price'
      : null;
  if (!method) return null;
  return { date: String(fi.date).slice(0, 10), label: fi.label, method };
}

/** Still held unissued in DVLA stock. */
export function isUnreleased(pd: PlateDetail | null | undefined): boolean {
  return pd?.firstIssue?.state === FirstIssueState.NEVER_ISSUED;
}

/**
 * The series the mark belongs to, in words: "August 1988 to July 1989" for a
 * dated mark, "introduced 1903" for a dateless one (year from the mark's own
 * pattern; the plate data says 1903 for every dateless mark).
 */
export function seriesPeriodOf(
  pd: PlateDetail | null | undefined,
  registration: string,
  dateless: boolean
): string | null {
  if (!pd) return null;
  if (dateless) {
    const year = seriesYearOf(pd, registration, true);
    return year ? `introduced ${year}` : null;
  }
  const period = pd.seriesPeriod || [pd.issueMonth, pd.issueYear].filter(Boolean).join(' ');
  return period || null;
}

/** The year a series began, for the timeline's origin tag. */
export function seriesYearOf(
  pd: PlateDetail | null | undefined,
  registration: string,
  dateless: boolean
): number | null {
  if (!pd) return null;
  if (dateless) return datelessSeriesYear(registration) || (pd.issueYear ? Number(pd.issueYear) : null);
  return pd.issueYear ? Number(pd.issueYear) : null;
}

/**
 * True when a dated record (a vehicle sighting, an advert) is earlier than
 * the plate's recorded first release, so it cannot be what it seems: a mark
 * in DVLA stock is on no car. Such records are flagged, never shown as fact.
 * Month precision: DVLA release dates are the first of the month.
 */
export function predatesRelease(value: string | null | undefined, release: FirstRelease | null): boolean {
  if (!value || !release) return false;
  return String(value).slice(0, 7) < release.date.slice(0, 7);
}
